import { AdData, PaymentData } from '../types';

interface TelegramMessage {
  chat_id: number;
  text: string;
  parse_mode?: 'Markdown' | 'HTML';
  disable_web_page_preview?: boolean;
  reply_markup?: {
    inline_keyboard: Array<Array<{
      text: string;
      url: string;
    }>>;
  };
}

interface TelegramPhoto {
  chat_id: number;
  photo: string;
  caption?: string;
  parse_mode?: 'Markdown' | 'HTML';
  reply_markup?: {
    inline_keyboard: Array<Array<{
      text: string;
      url: string;
    }>>;
  };
}

class TelegramService {
  private botToken: string;
  private adminChatId: string;
  private baseUrl: string;

  constructor() {
    this.botToken = process.env.REACT_APP_TELEGRAM_BOT_TOKEN || '';
    this.adminChatId = process.env.REACT_APP_TELEGRAM_ADMIN_CHAT_ID || '';
    this.baseUrl = `https://api.telegram.org/bot${this.botToken}`;
  }

  private getChatId(): number {
    return parseInt(this.adminChatId, 10);
  }

  private validateConfig(): boolean {
    if (!this.botToken || !this.adminChatId) {
      console.error('Telegram bot token or admin chat ID not configured');
      return false;
    }
    
    const chatId = parseInt(this.adminChatId, 10);
    if (isNaN(chatId)) {
      console.error('Admin chat ID must be a valid number');
      return false;
    }
    
    return true;
  }

  private async sendMessage(message: TelegramMessage): Promise<boolean> {
    if (!this.validateConfig()) {
      return false;
    }

    // 🔍 DEBUG: Log what we're sending to Telegram
    console.log('📨 SENDING MESSAGE TO TELEGRAM:');
    console.log('Chat ID:', message.chat_id);
    console.log('Parse Mode:', message.parse_mode);
    console.log('Text Content:', message.text);
    console.log('Text Length:', message.text.length);
    console.log('Full Message Object:', JSON.stringify(message, null, 2));

    try {
      const response = await fetch(`${this.baseUrl}/sendMessage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(message),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Telegram API Error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Telegram Response:', result);
      return result.ok;
    } catch (error) {
      console.error('❌ Error sending Telegram message:', error);
      if (error instanceof Error) {
        console.error('Message content that failed:', message.text.substring(0, 200));
      }
      return false;
    }
  }

  private dataURLtoBlob(dataURL: string): Blob {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  private async sendPhoto(photo: TelegramPhoto): Promise<boolean> {
    if (!this.validateConfig()) {
      return false;
    }

    // 🔍 DEBUG: Log photo message details
    console.log('📸 SENDING PHOTO TO TELEGRAM:');
    console.log('Chat ID:', photo.chat_id);
    console.log('Parse Mode:', photo.parse_mode);
    console.log('Caption:', photo.caption);
    console.log('Photo type:', photo.photo.startsWith('data:') ? 'Data URL' : 'Regular URL');
    console.log('Has reply_markup:', !!photo.reply_markup);

    try {
      // Handle data URLs by converting to multipart form data
      if (photo.photo.startsWith('data:')) {
        console.log('Converting data URL to blob for Telegram upload...');
        
        const formData = new FormData();
        formData.append('chat_id', photo.chat_id.toString());
        
        const blob = this.dataURLtoBlob(photo.photo);
        console.log('Blob created, size:', blob.size, 'bytes, type:', blob.type);
        
        // Determine file extension from blob type
        const extension = blob.type.includes('png') ? 'png' : 'jpg';
        formData.append('photo', blob, `image.${extension}`);
        
        if (photo.caption) {
          formData.append('caption', photo.caption);
        }
        
        if (photo.parse_mode) {
          formData.append('parse_mode', photo.parse_mode);
        }
        
        if (photo.reply_markup) {
          formData.append('reply_markup', JSON.stringify(photo.reply_markup));
        }

        console.log('Sending image to Telegram...');
        const response = await fetch(`${this.baseUrl}/sendPhoto`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Telegram API error:', response.status, errorText);
          throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
        }

        const result = await response.json();
        console.log('Image sent successfully to Telegram');
        return result.ok;
      } else {
        // Handle regular URLs
        const response = await fetch(`${this.baseUrl}/sendPhoto`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(photo),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        return result.ok;
      }
    } catch (error) {
      console.error('Error sending Telegram photo:', error);
      return false;
    }
  }

  private escapeMarkdown(text: string): string {
    // Escape special Markdown characters
    return text.replace(/[_*[\]()~`>#+-=|{}.!]/g, '\\$&');
  }

  private sanitizeHtmlForTelegram(html: string): string {
    if (!html) return '';
    
    console.log('🧹 SANITIZING HTML FOR TELEGRAM:');
    console.log('Input HTML:', html);
    
    let text = html;
    
    // Telegram HTML mode supports: <b>, <strong>, <i>, <em>, <u>, <ins>, <s>, <strike>, <del>, <code>, <pre>, <a>
    // Keep supported tags and remove unsupported ones
    text = text
      // Convert line breaks to newlines
      .replace(/<br\s*\/?>/gi, '\n')
      // Remove any unsupported HTML tags but keep supported ones
      .replace(/<(?!\/?(?:b|strong|i|em|u|ins|s|strike|del|code|pre|a)\b)[^>]*>/gi, '')
      // Decode HTML entities
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ')
      // Clean up multiple spaces but preserve newlines
      .replace(/ +/g, ' ')
      .trim();
    
    console.log('✅ Sanitized HTML:', text);
    return text;
  }

  private formatAdContent(adData: AdData): string {
    const { content, button, metadata } = adData;
    
    // 🔍 DEBUG: Log content processing
    console.log('🔧 FORMATTING AD CONTENT:');
    console.log('Raw content.html:', content.html);
    console.log('Raw content.text:', content.text);
    console.log('Content.html exists and not empty:', !!(content.html && content.html.trim()));
    
    // Always prioritize HTML content for admin notifications
    let adContent = '';
    if (content.html && content.html.trim()) {
      // Use HTML directly for Telegram with sanitization
      console.log('📝 Using HTML content, sanitizing...');
      adContent = this.sanitizeHtmlForTelegram(content.html);
      console.log('✅ Sanitized HTML content:', adContent);
    } else if (content.text && content.text.trim()) {
      // If no HTML content, escape plain text for HTML mode
      console.log('📝 Using plain text content, escaping...');
      adContent = this.escapeHtmlForTelegram(content.text);
      console.log('✅ Escaped text content:', adContent);
    }
    
    let message = `🆕 <b>NEW AD CREATED</b>\n\n`;
    message += `📝 <b>Ad Content:</b>\n${adContent}\n\n`;
    
    if (button.text && button.link) {
      message += `🔘 <b>Button:</b> ${this.escapeHtmlForTelegram(button.text)}\n`;
      message += `🔗 <b>Link:</b> ${this.escapeHtmlForTelegram(button.link)}\n\n`;
    }
    
    message += `📊 <b>Details:</b>\n`;
    message += `• Ad ID: <code>${adData.shortId || adData.id}</code>\n`;
    message += `• Character Count: ${metadata.characterCount}\n`;
    message += `• Status: ${adData.status.replace(/_/g, ' ')}\n`;
    message += `• Created: ${new Date(metadata.timestamp).toLocaleString()}\n`;
    
    if (adData.image) {
      message += `• Has Image: ✅\n`;
    }
    
    console.log('🎯 FINAL FORMATTED MESSAGE:', message);
    return message;
  }

  private escapeHtmlForTelegram(text: string): string {
    if (!text) return '';
    
    // Escape HTML characters for Telegram HTML mode
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private formatPaymentStatus(adId: string, paymentData: PaymentData, previousStatus?: string, telegramUsername?: string, shortAdId?: string): string {
    const statusEmojis = {
      pending_payment: '⏳',
      payment_initiated: '🔄',
      payment_completed: '✅',
      payment_failed: '❌'
    };

    const statusNames = {
      pending_payment: 'Pending Payment',
      payment_initiated: 'Payment Initiated',
      payment_completed: 'Payment Completed',
      payment_failed: 'Payment Failed'
    };

    let message = `${statusEmojis[paymentData.status]} <b>PAYMENT STATUS UPDATE</b>\n\n`;
    message += `📄 <b>Ad ID:</b> <code>${shortAdId || adId}</code>\n`;
    
    if (telegramUsername) {
      message += `📱 <b>Telegram:</b> @${telegramUsername}\n`;
    }
    
    if (previousStatus) {
      message += `🔄 <b>Status Changed:</b> ${statusNames[previousStatus as keyof typeof statusNames]} → ${statusNames[paymentData.status]}\n\n`;
    } else {
      message += `📊 <b>New Status:</b> ${statusNames[paymentData.status]}\n\n`;
    }
    
    let paymentMethodText = 'Bank Card (Stripe)';
    if (paymentData.method === 'crypto') {
      paymentMethodText = 'Cryptocurrency';
    }
    
    message += `💳 <b>Payment Method:</b> ${paymentMethodText}\n`;
    
    if (paymentData.details) {
      message += `📋 <b>Details:</b>\n`;
      
      if (paymentData.details.amount) {
        message += `• Amount: $${paymentData.details.amount}\n`;
      }
      
      if (paymentData.details.currency) {
        message += `• Currency: ${paymentData.details.currency}\n`;
      }
      
      if (paymentData.details.orderId) {
        message += `• Order ID: <code>${paymentData.details.orderId}</code>\n`;
      }
      
      if (paymentData.details.invoiceId) {
        message += `• Invoice ID: <code>${paymentData.details.invoiceId}</code>\n`;
      }
      
      if (paymentData.details.sessionId) {
        message += `• Session ID: <code>${paymentData.details.sessionId}</code>\n`;
      }
      
      if (paymentData.details.invoiceUrl) {
        message += `• Invoice URL: ${paymentData.details.invoiceUrl}\n`;
      }
      
      if (paymentData.details.error) {
        message += `• Error: ${paymentData.details.error}\n`;
      }
      
      const timestamp = paymentData.details.initiatedAt || paymentData.details.failedAt;
      if (timestamp) {
        message += `• Time: ${new Date(timestamp).toLocaleString()}\n`;
      }
    }
    
    return message;
  }

  private isValidUrl(url: string): boolean {
    if (!url || typeof url !== 'string' || url.length < 4) return false;
    
    // Check for common invalid cases
    if (url.length === 1 || url.includes(' ') || !url.includes('.')) {
      return false;
    }
    
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch (error) {
      return false;
    }
  }

  private isValidButtonText(text: string): boolean {
    return Boolean(text && typeof text === 'string' && text.trim().length > 0);
  }

  private createInlineKeyboard(buttonText: string, buttonLink: string) {
    // Only create keyboard if both text and URL are valid
    if (!this.isValidButtonText(buttonText)) {
      console.warn('Invalid button text detected:', buttonText);
      return undefined;
    }
    
    if (!this.isValidUrl(buttonLink)) {
      console.warn('Invalid button URL detected:', buttonLink);
      return undefined;
    }
    
    return {
      inline_keyboard: [[{
        text: buttonText.trim(),
        url: buttonLink
      }]]
    };
  }

  private async sendAdPreview(adData: AdData): Promise<boolean> {
    const { content, button, image } = adData;
    
    // 🔍 DEBUG: Log preview content processing
    console.log('🎬 SENDING AD PREVIEW:');
    console.log('Preview content.html:', content.html);
    console.log('Preview content.text:', content.text);
    
    // Always prioritize HTML content for admin preview
    let adText = '';
    if (content.html && content.html.trim()) {
      // Use HTML directly for Telegram with sanitization
      console.log('📝 Using HTML content for preview...');
      adText = this.sanitizeHtmlForTelegram(content.html);
      console.log('✅ Preview HTML content:', adText);
    } else if (content.text && content.text.trim()) {
      // If no HTML content, escape plain text for HTML mode
      console.log('📝 Using plain text content for preview...');
      adText = this.escapeHtmlForTelegram(content.text);
      console.log('✅ Preview text content:', adText);
    }
    
    // Check if image exists (allow data URLs too)
    const hasImage = image && image.src;
    const hasValidButton = this.isValidButtonText(button.text) && this.isValidUrl(button.link);
    
    // Log button validation for debugging
    if (button.text || button.link) {
      if (!hasValidButton) {
        console.warn('⚠️ Invalid button detected - will send without button:');
        console.warn('  Button text:', button.text);
        console.warn('  Button link:', button.link);
        console.warn('  Text valid:', this.isValidButtonText(button.text));
        console.warn('  URL valid:', this.isValidUrl(button.link));
      } else {
        console.log('✅ Valid button detected:', button.text, '→', button.link);
      }
    }

    try {
      let adSent = false;

      // Type 3: Text, Image, and Button (only if button URL is valid)
      if (hasImage && hasValidButton && adText) {
        console.log('🖼️ Sending photo with button...');
        const keyboard = this.createInlineKeyboard(button.text, button.link);
        adSent = await this.sendPhoto({
          chat_id: this.getChatId(),
          photo: image!.src,
          caption: adText,
          parse_mode: 'HTML',
          reply_markup: keyboard
        });
      }
      // Type 4: Text and Image only (fallback or no button)
      else if (hasImage && adText) {
        console.log('🖼️ Sending photo without button...');
        adSent = await this.sendPhoto({
          chat_id: this.getChatId(),
          photo: image!.src,
          caption: adText,
          parse_mode: 'HTML'
        });
      }
      // Type 2: Button and Text (no image, only if button URL is valid)
      else if (hasValidButton && adText) {
        console.log('📝 Sending text with button...');
        const keyboard = this.createInlineKeyboard(button.text, button.link);
        adSent = await this.sendMessage({
          chat_id: this.getChatId(),
          text: adText,
          parse_mode: 'HTML',
          disable_web_page_preview: true,
          reply_markup: keyboard
        });
      }
      // Type 1: Just Text (fallback)
      else if (adText) {
        console.log('📝 Sending text only...');
        adSent = await this.sendMessage({
          chat_id: this.getChatId(),
          text: adText,
          parse_mode: 'HTML',
          disable_web_page_preview: true
        });
      }
      
      return adSent;
    } catch (error) {
      console.error('❌ Error sending ad preview:', error);
      
      // 🔄 FALLBACK: Try to send a plain text notification to admin
      console.log('🔄 Attempting fallback plain text notification...');
      try {
        const fallbackMessage = `📢 <b>AD PREVIEW (Fallback)</b>\n\n${adText || 'Ad content available'}\n\n⚠️ <i>Note: Original preview failed due to invalid button URL or image issue</i>`;
        
        const fallbackSent = await this.sendMessage({
          chat_id: this.getChatId(),
          text: fallbackMessage,
          parse_mode: 'HTML',
          disable_web_page_preview: true
        });
        
        if (fallbackSent) {
          console.log('✅ Fallback notification sent successfully');
          return true;
        } else {
          console.log('❌ Fallback notification also failed');
          return false;
        }
      } catch (fallbackError) {
        console.error('❌ Fallback notification failed:', fallbackError);
        return false;
      }
    }
  }

  // Public methods
  async notifyAdCreated(adData: AdData): Promise<boolean> {
    // Run notification in background - don't block calling code
    this.backgroundNotifyAdCreated(adData);
    
    // Return true immediately to not block the calling flow
    return true;
  }

  // Background method for ad creation notification
  private async backgroundNotifyAdCreated(adData: AdData): Promise<void> {
    try {
      console.log('🔄 Background Telegram notification started for ad:', adData.id);
      
      // Send admin notification first - always use HTML content and HTML parse mode
      const message = this.formatAdContent(adData);
      
      // Send with HTML parse mode (prioritizes content.html over content.text)
      let adminNotificationSent = await this.sendMessage({
        chat_id: this.getChatId(),
        text: message,
        parse_mode: 'HTML',
        disable_web_page_preview: true
      });

      // If HTML failed, try plain text as fallback
      if (!adminNotificationSent) {
        console.log('HTML parse mode failed, trying plain text...');
        adminNotificationSent = await this.sendMessage({
          chat_id: this.getChatId(),
          text: message.replace(/<[^>]*>/g, ''), // Remove HTML tags
          disable_web_page_preview: true
        });
      }

      // Send ad preview showing how it will actually look (uses HTML content)
      // This is separate from the main notification, so if it fails, the admin still gets notified
      try {
        console.log('📸 Sending ad preview...');
        const previewSent = await this.sendAdPreview(adData);
        if (previewSent) {
          console.log('✅ Ad preview sent successfully');
        } else {
          console.log('⚠️ Ad preview failed, but main notification was sent');
        }
      } catch (previewError) {
        console.error('❌ Ad preview failed with error:', previewError);
        console.log('ℹ️ Main notification was still sent to admin');
      }

      if (adminNotificationSent) {
        console.log('✅ Background Telegram notification completed for ad:', adData.id);
      } else {
        console.log('❌ Background Telegram notification failed for ad:', adData.id);
      }
    } catch (error) {
      console.error('❌ Background Telegram notification error:', error);
      // Don't throw error to avoid breaking the calling flow
    }
  }

  async notifyPaymentStatusChange(adId: string, paymentData: PaymentData, previousStatus?: string, telegramUsername?: string, shortAdId?: string): Promise<boolean> {
    // Run notification in background - don't block calling code
    this.backgroundNotifyPaymentStatusChange(adId, paymentData, previousStatus, telegramUsername, shortAdId);
    
    // Return true immediately to not block the calling flow
    return true;
  }

  // Background method for payment status notification
  private async backgroundNotifyPaymentStatusChange(adId: string, paymentData: PaymentData, previousStatus?: string, telegramUsername?: string, shortAdId?: string): Promise<void> {
    try {
      console.log('🔄 Background payment status notification started for ad:', adId);
      
      const message = this.formatPaymentStatus(adId, paymentData, previousStatus, telegramUsername, shortAdId);
      
      // Try sending with HTML first, fallback to plain text if it fails
      let sent = await this.sendMessage({
        chat_id: this.getChatId(),
        text: message,
        parse_mode: 'HTML',
        disable_web_page_preview: true
      });

      // If HTML failed, try plain text
      if (!sent) {
        console.log('HTML failed, trying plain text...');
        sent = await this.sendMessage({
          chat_id: this.getChatId(),
          text: message.replace(/<[^>]*>/g, ''), // Remove HTML tags
          disable_web_page_preview: true
        });
      }

      if (sent) {
        console.log('✅ Background payment status notification completed for ad:', adId);
      } else {
        console.log('❌ Background payment status notification failed for ad:', adId);
      }
    } catch (error) {
      console.error('❌ Background payment status notification error:', error);
      // Don't throw error to avoid breaking the calling flow
    }
  }

  async sendCustomMessage(message: string): Promise<boolean> {
    return await this.sendMessage({
      chat_id: this.getChatId(),
      text: message,
      parse_mode: 'Markdown',
      disable_web_page_preview: true
    });
  }

  // Test connection
  async testConnection(): Promise<boolean> {
    if (!this.validateConfig()) {
      return false;
    }

    try {
      const response = await fetch(`${this.baseUrl}/getMe`);
      const result = await response.json();
      
      if (result.ok) {
        console.log('Telegram bot connected successfully:', result.result);
        return true;
      } else {
        console.error('Telegram bot connection failed:', result);
        return false;
      }
    } catch (error) {
      console.error('Error testing Telegram connection:', error);
      return false;
    }
  }
}

// Export singleton instance
export const telegramService = new TelegramService();
export default telegramService; 